customElements.whenDefined("x-drawer").then(() => {
  const Drawer = customElements.get("x-drawer");
  const STORAGE_KEY = "maceoo:luxe-nav-state";

  class LuxeNav extends Drawer {
    #openButton = null;
    #rootScrollTop = 0;

    connectedCallback() {
      super.connectedCallback();

      this.addEventListener("click", this.#onClick, {
        signal: this.abortController.signal,
      });
      this.addEventListener("dialog:after-hide", this.#onAfterHide, {
        signal: this.abortController.signal,
      });

      if (window.Shopify && window.Shopify.designMode) {
        this.addEventListener("shopify:block:select", this.#onBlockSelect, {
          signal: this.abortController.signal,
        });
      }

      this.#restoreState();
    }

    get level() {
      return parseInt(this.dataset.level || "1", 10);
    }

    set level(value) {
      this.dataset.level = String(value);
    }

    #onClick = (event) => {
      const openButton = event.target.closest("[data-luxe-open-panel]");

      if (openButton) {
        this.#openPanel(openButton);
        return;
      }

      if (event.target.closest('[data-action="breadcrumb-back"]')) {
        this.#closePanel();
        return;
      }

      const searchButton = event.target.closest("[data-luxe-search]");

      if (searchButton) {
        this.#openSearch(searchButton.dataset.luxeSearch);
      }
    };

    #openPanel(button, focusPanel = true) {
      const panel = this.#byId(button.getAttribute("aria-controls"));
      if (!panel) return;

      this.#openButton = button;

      this.querySelectorAll("[data-luxe-open-panel]").forEach((other) => {
        other.setAttribute(
          "aria-expanded",
          other === button ? "true" : "false",
        );
      });

      const root = this.querySelector(".luxe-nav__panel--root");
      if (root) root.hidden = true;

      this.querySelectorAll(".luxe-nav__panel--sub").forEach((other) => {
        other.hidden = other !== panel;
      });

      const body = this.querySelector(".luxe-nav__body");
      if (body) {
        if (this.level === 1) this.#rootScrollTop = body.scrollTop;
        body.scrollTop = 0;
      }

      this.#setBreadcrumb(button.dataset.title);
      this.level = 2;
      this.#saveState();

      if (focusPanel) {
        panel.querySelector("a[href], button")?.focus({ preventScroll: true });
      }
    }

    #closePanel() {
      this.#openButton = null;

      this.querySelectorAll(".luxe-nav__panel--sub").forEach((panel) => {
        panel.hidden = true;
      });
      this.querySelectorAll("[data-luxe-open-panel]").forEach((button) => {
        button.setAttribute("aria-expanded", "false");
      });

      const root = this.querySelector(".luxe-nav__panel--root");
      if (root) root.hidden = false;

      const body = this.querySelector(".luxe-nav__body");
      if (body) body.scrollTop = this.#rootScrollTop;

      this.#setBreadcrumb(null);
      this.level = 1;
      this.#saveState();
    }

    #openSearch(id) {
      const search = id ? document.getElementById(id) : null;
      if (!search) return;

      this.hide().then(() => search.show());
    }

    #onAfterHide = () => {
      this.#rootScrollTop = 0;
      this.querySelectorAll(".luxe-nav__body, .luxe-nav__panel").forEach(
        (element) => {
          element.scrollTop = 0;
        },
      );
    };

    #saveState() {
      try {
        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            panel: this.#openButton?.dataset.title || null,
          }),
        );
      } catch {}
    }

    #restoreState() {
      let state = null;
      try {
        state = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
      } catch {
        return;
      }
      if (!state || !state.panel) return;

      const button = Array.from(
        this.querySelectorAll("[data-luxe-open-panel]"),
      ).find((candidate) => candidate.dataset.title === state.panel);

      if (button) this.#openPanel(button, false);
    }

    #setBreadcrumb(title) {
      const breadcrumb = this.querySelector(".luxe-nav__breadcrumb");
      if (!breadcrumb) return;

      breadcrumb.hidden = !title;

      const slot = breadcrumb.querySelector("[data-luxe-panel-title]");
      if (slot) slot.textContent = title || "";
    }

    #byId(id) {
      return id ? this.querySelector(`#${CSS.escape(id)}`) : null;
    }

    #onBlockSelect = (event) => {
      if (!event.target.closest(".luxe-nav__tile")) return;

      this.show();
      this.#closePanel();
    };
  }

  if (!customElements.get("luxe-nav")) {
    customElements.define("luxe-nav", LuxeNav);
  }
});
